import type {
  CreditDeclineAction,
  CreditDeclineActionType,
  CreditDeclineBreakdownType,
  CreditDeclineExternalObligationItem,
  CreditDeclineInfo,
} from '@/shared/types/credit-decline';
import {
  CREDIT_DECLINE_ACTION_TYPE,
  CREDIT_DECLINE_BREAKDOWN_ORDER,
} from '@/shared/types/credit-decline';
import { isValidDeeplinkUrl } from '@/shared/lib/deeplink';

const CREDIT_DECLINE_BREAKDOWN_TYPE_MAP: Record<string, CreditDeclineBreakdownType> =
  {
    income: 'INCOME',
    INCOME: 'INCOME',
    external_obligations: 'EXTERNAL_OBLIGATIONS',
    EXTERNAL_OBLIGATIONS: 'EXTERNAL_OBLIGATIONS',
    internal_obligations: 'INTERNAL_OBLIGATIONS',
    INTERNAL_OBLIGATIONS: 'INTERNAL_OBLIGATIONS',
    expenses: 'EXPENSES',
    EXPENSES: 'EXPENSES',
  };

const DBR_DECLINE_CODES = new Set(['900012', '900026']);
const AFFORDABILITY_DECLINE_CODES = new Set([
  '900013',
  '900027',
  '900146',
  '900147',
]);

export type CreditDeclineMoney = {
  amount: number;
  currency: string;
  amountMax?: number;
};

export const parseCreditDeclineMoney = (
  raw: unknown,
): CreditDeclineMoney | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const value = raw as Record<string, unknown>;
  const amountRaw = value.amount;
  const amount =
    typeof amountRaw === 'number'
      ? amountRaw
      : typeof amountRaw === 'string'
        ? Number(amountRaw)
        : NaN;
  if (!Number.isFinite(amount)) {
    return null;
  }
  const amountMaxRaw = value.amount_max ?? value.amountMax;
  const amountMax =
    typeof amountMaxRaw === 'number'
      ? amountMaxRaw
      : typeof amountMaxRaw === 'string'
        ? Number(amountMaxRaw)
        : undefined;

  return {
    amount,
    currency: String(value.currency ?? ''),
    ...(amountMax !== undefined && Number.isFinite(amountMax)
      ? { amountMax }
      : {}),
  };
};

const resolveDeclineExplainer = (
  data: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  const raw =
    data.creditDeclineExplainer ??
    data.credit_decline_explainer ??
    data.declineExplainer ??
    data.decline_explainer;

  return raw && typeof raw === 'object'
    ? (raw as Record<string, unknown>)
    : undefined;
};

const resolveDeclineCode = (data: Record<string, unknown>): string =>
  String(
    data.creditDeclineCode ??
      data.credit_decline_code ??
      data.declineCode ??
      data.decline_code ??
      '',
  ).trim();

const resolveRecommendedLimit = (
  declineCode: string,
  explainer: Record<string, unknown> | undefined,
  data: Record<string, unknown>,
): CreditDeclineMoney | null => {
  const explicit = parseCreditDeclineMoney(
    data.recommendedLimit ?? data.recommended_limit,
  );
  if (explicit) {
    return explicit;
  }

  const dbr = parseCreditDeclineMoney(explainer?.dbr);
  const affordability = parseCreditDeclineMoney(explainer?.affordability);

  if (DBR_DECLINE_CODES.has(declineCode)) {
    return dbr ?? affordability ?? null;
  }
  if (AFFORDABILITY_DECLINE_CODES.has(declineCode)) {
    return affordability ?? dbr ?? null;
  }
  return dbr ?? affordability ?? null;
};

const resolveOrderAmount = (
  data: Record<string, unknown>,
  fallbackOrderAmount: CreditDeclineMoney | null,
): CreditDeclineMoney | null => {
  return (
    parseCreditDeclineMoney(data.orderAmount ?? data.order_amount) ??
    parseCreditDeclineMoney(data.requestedAmount ?? data.requested_amount) ??
    fallbackOrderAmount
  );
};

const parseExternalObligationItems = (
  external: Record<string, unknown>,
): CreditDeclineExternalObligationItem[] => {
  if (!Array.isArray(external.items)) {
    return [];
  }

  return external.items
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }
      const entry = item as Record<string, unknown>;
      const monthlyInstalment = parseCreditDeclineMoney(
        entry.monthlyInstalment ?? entry.monthly_instalment,
      );
      if (!monthlyInstalment) {
        return null;
      }
      return {
        name: String(entry.name ?? '').trim(),
        type: String(entry.type ?? '').trim(),
        status: String(entry.status ?? '').trim(),
        monthlyInstalment,
      };
    })
    .filter(
      (item): item is CreditDeclineExternalObligationItem => item !== null,
    );
};

const KNOWN_ACTION_TYPES = new Set<string>(
  Object.values(CREDIT_DECLINE_ACTION_TYPE),
);

const parseNullableBoolean = (raw: unknown): boolean | null =>
  typeof raw === 'boolean' ? raw : null;

/**
 * Parses explainer.actions into validated {@link CreditDeclineAction}s. Entries
 * with an unknown type, missing/invalid deeplink, or duplicate type are dropped
 * so the UI never renders a broken or untrusted action.
 */
export const parseCreditDeclineActions = (
  explainer: Record<string, unknown> | undefined,
): CreditDeclineAction[] => {
  const rawActions = explainer?.actions;
  if (!Array.isArray(rawActions)) {
    return [];
  }

  const seenTypes = new Set<CreditDeclineActionType>();
  const actions: CreditDeclineAction[] = [];

  for (const item of rawActions) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const entry = item as Record<string, unknown>;
    const type = String(entry.type ?? '').trim();
    if (!KNOWN_ACTION_TYPES.has(type)) {
      continue;
    }
    const actionType = type as CreditDeclineActionType;
    if (seenTypes.has(actionType)) {
      continue;
    }

    const url = String(entry.action ?? '').trim();
    if (!isValidDeeplinkUrl(url)) {
      continue;
    }

    seenTypes.add(actionType);
    actions.push({
      type: actionType,
      action: url,
      eligible: parseNullableBoolean(entry.eligible),
      mandatory: parseNullableBoolean(entry.mandatory),
    });
  }

  return actions;
};

const sparseBreakdownFromMap = (
  byType: Map<CreditDeclineBreakdownType, CreditDeclineMoney>,
): CreditDeclineInfo['breakdown'] =>
  CREDIT_DECLINE_BREAKDOWN_ORDER.filter((type) => byType.has(type)).map(
    (type) => ({
      type,
      amount: byType.get(type)!,
    }),
  );

const buildBreakdownFromExplainer = (
  explainer: Record<string, unknown> | undefined,
  rootData: Record<string, unknown>,
): {
  breakdown: CreditDeclineInfo['breakdown'];
  externalObligationItems: CreditDeclineExternalObligationItem[];
} => {
  const byType = new Map<CreditDeclineBreakdownType, CreditDeclineMoney>();
  const externalObligationItems: CreditDeclineExternalObligationItem[] = [];

  if (explainer) {
    const income = parseCreditDeclineMoney(explainer.income);
    if (income) {
      byType.set('INCOME', income);
    }

    const externalObligations =
      explainer.externalObligations ?? explainer.external_obligations;
    if (externalObligations && typeof externalObligations === 'object') {
      const external = externalObligations as Record<string, unknown>;
      const total =
        parseCreditDeclineMoney(external.totalMonthlyInstalment) ??
        parseCreditDeclineMoney(external.total_monthly_instalment) ??
        parseCreditDeclineMoney(external);
      if (total) {
        byType.set('EXTERNAL_OBLIGATIONS', total);
      }
      externalObligationItems.push(...parseExternalObligationItems(external));
    }

    const internalObligations =
      explainer.internalObligations ?? explainer.internal_obligations;
    const internalAmount = parseCreditDeclineMoney(internalObligations);
    if (internalAmount) {
      byType.set('INTERNAL_OBLIGATIONS', internalAmount);
    }

    const expensesInExplainer = explainer.expenses;
    if (expensesInExplainer && typeof expensesInExplainer === 'object') {
      const expensesObj = expensesInExplainer as Record<string, unknown>;
      const estimated =
        parseCreditDeclineMoney(expensesObj.estimatedMonthlyExpenses) ??
        parseCreditDeclineMoney(expensesObj.estimated_monthly_expenses) ??
        parseCreditDeclineMoney(expensesObj);
      if (estimated) {
        byType.set('EXPENSES', estimated);
      }
    }
  }

  // Legacy: expenses at credit_decline_info root (pre-final contract)
  if (!byType.has('EXPENSES')) {
    const expensesRoot = rootData.expenses;
    if (expensesRoot && typeof expensesRoot === 'object') {
      const expensesObj = expensesRoot as Record<string, unknown>;
      const estimated =
        parseCreditDeclineMoney(expensesObj.estimatedMonthlyExpenses) ??
        parseCreditDeclineMoney(expensesObj.estimated_monthly_expenses);
      if (estimated) {
        byType.set('EXPENSES', estimated);
      }
    }
  }

  return {
    breakdown: sparseBreakdownFromMap(byType),
    externalObligationItems,
  };
};

const buildBreakdownFromFlatArray = (
  breakdownRaw: unknown,
): CreditDeclineInfo['breakdown'] => {
  if (!Array.isArray(breakdownRaw)) {
    return [];
  }

  const byType = new Map<CreditDeclineBreakdownType, CreditDeclineMoney>();
  for (const item of breakdownRaw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const entry = item as Record<string, unknown>;
    const typeKey = String(entry.type ?? '');
    const mappedType = CREDIT_DECLINE_BREAKDOWN_TYPE_MAP[typeKey];
    const amount = parseCreditDeclineMoney(entry.amount);
    if (mappedType && amount) {
      byType.set(mappedType, amount);
    }
  }

  return sparseBreakdownFromMap(byType);
};

/**
 * Normalizes checkout `credit_decline_info` into the FE view model for decline UI.
 *
 * Final contract (snake_case from BE):
 * - credit_decline_code
 * - credit_decline_explainer: { dbr, affordability, income, internal_obligations,
 *   external_obligations: { total_monthly_instalment, items[] }, expenses }
 *
 * Also accepts legacy field names (decline_code, decline_explainer, root expenses).
 */
export function parseCreditDeclineInfo(
  raw: unknown,
  fallbackOrderAmount: CreditDeclineMoney | null = null,
): CreditDeclineInfo | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const data = raw as Record<string, unknown>;
  const explainer = resolveDeclineExplainer(data);
  const declineCode = resolveDeclineCode(data);

  const orderAmount = resolveOrderAmount(data, fallbackOrderAmount);
  const recommendedLimit = resolveRecommendedLimit(declineCode, explainer, data);

  const flatBreakdown = buildBreakdownFromFlatArray(data.breakdown);
  const { breakdown, externalObligationItems } = Array.isArray(data.breakdown)
    ? { breakdown: flatBreakdown, externalObligationItems: [] }
    : buildBreakdownFromExplainer(explainer, data);

  if (!declineCode) {
    return null;
  }

  const actions = parseCreditDeclineActions(explainer);

  return {
    declineCode,
    declineDescription:
      String(data.declineDescription ?? data.decline_description ?? '').trim() ||
      undefined,
    headline: String(data.headline ?? '').trim() || undefined,
    subtitle: String(data.subtitle ?? '').trim() || undefined,
    orderAmount,
    recommendedLimit,
    breakdown,
    ...(externalObligationItems.length > 0
      ? { externalObligationItems }
      : {}),
    actions,
  };
}
